import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useLocation } from "react-router-dom"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useSidebarWidth } from "@/shared/hooks/useSidebarWidth"
import { useScrollbarAutoHide } from "@/shared/hooks/useScrollbarAutoHide"
import { useScrollRestore } from "@/shared/hooks/useScrollRestore"
import { useViewContext } from "@/app/stores/viewContext"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { useViewContextSync } from "@/app/hooks/useViewContextSync"
import { useCapacitorLifecycle } from "@/app/hooks/useCapacitorLifecycle"
import { useNavigationHistory } from "@/app/hooks/useNavigationHistory"
import { useVersionCheck } from "@/app/hooks/useVersionCheck"
import { useKeyboardShortcuts } from "@/shared/hooks/useKeyboardShortcuts"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { useClipboardPaste } from "@/shared/hooks/useClipboardPaste"
import { useSidebarDragOver } from "@/features/dnd/useSidebarDragOver"
import { ReminderPermissionDialog } from "@/features/reminders/ReminderPermissionDialog"
import { useLanguageSync } from "@/i18n/useLanguageSync"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"
import { useZoomEffect } from "@/shared/hooks/useZoomEffect"
import { useAccentColorEffect } from "@/shared/hooks/useAccentColorEffect"
import { useAppearanceEffect } from "@/shared/hooks/useAppearanceEffect"
import { DeferredMount } from "@/app/components/DeferredMount"
import { DeferredHooks } from "@/app/layout/DeferredHooks"
import {
  MainScrollProvider,
  useMainScroll,
} from "@/app/providers/MainScrollProvider"
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
  usePanelRef,
} from "@/app/components/ui/resizable"
import { PullToRefresh } from "@/features/pull-to-refresh/PullToRefresh"
import { Sidebar } from "@/features/navigation/Sidebar"
import { SidebarSkeleton } from "@/features/navigation/SidebarSkeleton"
import { MobileDrawer } from "@/features/navigation/MobileDrawer"
import { TopNavBar } from "@/features/navigation/TopNavBar"
import { ShortcutHelpOverlay } from "@/shared/components/ShortcutHelpOverlay"
import { StatusBarTapZone } from "@/shared/components/StatusBarTapZone"
import { useScrollToTop } from "@/shared/hooks/useScrollToTop"
import { ActionPanel } from "@/features/action-panel/ActionPanel"
import { AddFab } from "@/features/quick-add/AddFab"
import { useFabState } from "@/features/quick-add/useFabState"
import { UpdatePrompt } from "@/features/update/UpdatePrompt"
import { SyncStatusProgressBar } from "@/features/sync/SyncStatusProgressBar"
import { ChatPanel } from "@/features/ai-chat/ChatPanel"
import { MobileChatDrawer } from "@/features/ai-chat/MobileChatDrawer"
import { FocusBanner } from "@/features/focus/FocusBanner"
import { GlobalTimerBar } from "@/features/time-tracking/GlobalTimerBar"
import { useActiveTimers } from "@/features/time-tracking/useActiveTimer"

interface AppLayoutProps {
  children: ReactNode
}

/**
 * App shell layout — responsive split between desktop and mobile.
 *
 * Desktop (≥768px):
 *   ResizablePanelGroup with sidebar panel + main content panel.
 *   Sidebar width (px) persisted to Dexie setting_local.
 *
 * Mobile (<768px):
 *   Full-screen swipeable drawer + top nav bar + main content.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const { isDesktop } = useBreakpoint()
  const { pathname } = useLocation()

  // CRITICAL hooks — must run immediately for correct first paint
  useViewContextSync()
  useCapacitorLifecycle()
  useNavigationHistory()
  useKeyboardShortcuts()
  useClipboardPaste()
  useLanguageSync()
  useZoomEffect()
  useAccentColorEffect()
  useAppearanceEffect()

  // Flush pending completions immediately on route change (v1 pattern)
  useEffect(() => {
    void usePendingCompletions.getState().flushAll()
  }, [pathname])

  const versionCheck = useVersionCheck()

  const overlay = (
    <>
      <ShortcutHelpOverlay />
      <UpdatePrompt versionCheck={versionCheck} />
      <ReminderPermissionDialog />
      <DeferredMount>
        <DeferredHooks />
      </DeferredMount>
    </>
  )

  if (isDesktop) {
    return (
      <MainScrollProvider>
        <SyncStatusProgressBar />
        <Suspense
          fallback={
            <div className="flex h-dvh">
              <div className="w-[280px] shrink-0">
                <SidebarSkeleton />
              </div>
              <div className="flex-1" />
            </div>
          }
        >
          <DesktopLayout>{children}</DesktopLayout>
        </Suspense>
        {overlay}
      </MainScrollProvider>
    )
  }

  return (
    <MainScrollProvider>
      <MobileLayout>{children}</MobileLayout>
      {overlay}
    </MainScrollProvider>
  )
}

// ---------------------------------------------------------------------------
// Desktop: Resizable sidebar + main content
// ---------------------------------------------------------------------------

function DesktopLayout({ children }: { children: ReactNode }) {
  const { width, setWidth, minWidth, maxWidth } = useSidebarWidth()
  const { scrollRef } = useMainScroll()
  const { onScroll } = useScrollbarAutoHide({ externalRef: scrollRef })
  useScrollRestore(scrollRef)
  const setSearchOpen = useViewContext((s) => s.setSearchOpen)
  const collapsed = useViewContext((s) => s.desktopSidebarCollapsed)
  const aiChatOpen = useViewContext((s) => s.aiChatOpen)
  const toggleAiChat = useViewContext((s) => s.toggleAiChat)
  const setDesktopSidebarCollapsed = useViewContext(
    (s) => s.setDesktopSidebarCollapsed,
  )
  const toggleDesktopSidebar = useViewContext((s) => s.toggleDesktopSidebar)
  const { value: isColorVariant } = useSettingBoolean("#theme-variant-color")
  const iconMode = isColorVariant ? ("color" as const) : ("monochrome" as const)

  // Expose theme variant on <html> for CSS-driven styling (e.g. time chip colors)
  useEffect(() => {
    document.documentElement.dataset.themeVariant = isColorVariant ? "color" : "monochrome"
  }, [isColorVariant])

  // Detect sidebar drop targets during drag
  useSidebarDragOver()

  const sidebarPanelRef = usePanelRef()
  const sidebarElementRef = useRef<HTMLDivElement>(null)
  const hasMounted = useRef(false)
  const isToggling = useRef(false)
  const prevCollapsed = useRef(collapsed)
  const [pillHighlight, setPillHighlight] = useState(false)

  // Chat panel ref for collapse/expand
  const chatPanelRef = usePanelRef()
  const chatElementRef = useRef<HTMLDivElement>(null)

  // Persist chat panel width
  const CHAT_WIDTH_KEY = "totodo:ai-chat-width"
  const CHAT_DEFAULT_WIDTH = 360
  const chatWidthRef = useRef(() => {
    try {
      const v = localStorage.getItem(CHAT_WIDTH_KEY)
      if (v) { const n = Number(v); if (n >= 280 && n <= 600) return n }
    } catch {}
    return CHAT_DEFAULT_WIDTH
  })
  const savedChatWidth = chatWidthRef.current()

  const handleChatResize = useCallback((size: { inPixels: number }) => {
    if (size.inPixels > 0) {
      try { localStorage.setItem(CHAT_WIDTH_KEY, String(Math.round(size.inPixels))) } catch {}
    }
  }, [])

  // Initial sync on mount — collapse chat instantly if saved state is closed
  useEffect(() => {
    if (!aiChatOpen) {
      chatPanelRef.current?.collapse()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, [])

  // Animate chat collapse/expand on toggle (after mount)
  useEffect(() => {
    if (!hasMounted.current) return
    const el = chatElementRef.current
    if (el) {
      el.style.transition = "flex-grow 300ms cubic-bezier(0.4, 0, 0.2, 1)"
      const cleanup = () => { el.style.transition = "" }
      el.addEventListener("transitionend", cleanup, { once: true })
      setTimeout(cleanup, 400)
    }
    if (aiChatOpen) {
      chatPanelRef.current?.expand()
    } else {
      chatPanelRef.current?.collapse()
    }
  }, [aiChatOpen, chatPanelRef])

  // Initial sync on mount — collapse instantly (no animation) if saved state is collapsed
  useEffect(() => {
    if (collapsed) {
      sidebarPanelRef.current?.collapse()
    }
    // Small delay to avoid animating the initial layout
    requestAnimationFrame(() => {
      hasMounted.current = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, [])

  // Animate collapse/expand on toggle (after mount)
  useEffect(() => {
    if (!hasMounted.current) return
    if (collapsed === prevCollapsed.current) return
    prevCollapsed.current = collapsed

    // Add transition to the panel's outer flex element for smooth animation
    const el = sidebarElementRef.current
    if (el) {
      el.style.transition = "flex-grow 300ms cubic-bezier(0.4, 0, 0.2, 1)"
      const cleanup = () => {
        el.style.transition = ""
      }
      el.addEventListener("transitionend", cleanup, { once: true })
      setTimeout(cleanup, 400) // fallback
    }

    isToggling.current = true
    if (collapsed) {
      sidebarPanelRef.current?.collapse()
      // Highlight the edge pill briefly after animation
      setTimeout(() => {
        setPillHighlight(true)
        setTimeout(() => setPillHighlight(false), 1000)
      }, 300)
    } else {
      sidebarPanelRef.current?.expand()
    }
    setTimeout(() => {
      isToggling.current = false
    }, 350)
  }, [collapsed, sidebarPanelRef])

  // Sync library-initiated collapse (user dragged below minWidth) with state
  const handleResize = useCallback(
    (size: { asPercentage: number; inPixels: number }) => {
      if (isToggling.current) return
      if (size.inPixels > 0) {
        setWidth(size.inPixels)
      }
      const panelCollapsed = sidebarPanelRef.current?.isCollapsed() ?? false
      if (panelCollapsed && !collapsed) {
        setDesktopSidebarCollapsed(true)
      } else if (!panelCollapsed && collapsed) {
        setDesktopSidebarCollapsed(false)
      }
    },
    [collapsed, setDesktopSidebarCollapsed, setWidth, sidebarPanelRef],
  )

  const handleRefresh = useCallback(async () => {
    setSearchOpen(true)
  }, [setSearchOpen])

  return (
    <div className="h-dvh relative flex flex-col">
      {/* Immersive focus banner — above all content, pushes layout down */}
      <FocusBanner />

      {/* Edge pill — visible when sidebar is collapsed */}
      {collapsed && (
        <button
          type="button"
          data-panel-pill
          onClick={toggleDesktopSidebar}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center h-12 w-3 cursor-pointer group"
          aria-label="Show sidebar (⌘\)"
        >
          <div
            className={
              "h-10 w-1.5 rounded-full transition-all duration-300 group-hover:bg-foreground/25 group-hover:w-2 " +
              (pillHighlight
                ? "bg-foreground/30 w-2 scale-y-125"
                : "bg-foreground/10")
            }
          />
        </button>
      )}

      <ResizablePanelGroup orientation="horizontal" className="focus-banner-dim flex-1 min-h-0">
        <ResizablePanel
          panelRef={sidebarPanelRef}
          elementRef={sidebarElementRef}
          collapsible
          collapsedSize="0px"
          defaultSize={`${width}px`}
          minSize={`${minWidth}px`}
          maxSize={`${maxWidth}px`}
          onResize={handleResize}
          className="h-full overflow-hidden"
        >
          <div className="h-full" style={{ zoom: "var(--menu-zoom, 1)" }}>
            <Sidebar iconMode={iconMode} />
          </div>
        </ResizablePanel>

        <ResizableHandle
          withHandle={!collapsed}
          className="group z-20"
          onPillClick={toggleDesktopSidebar}
        />

        <ResizablePanel className="flex flex-col overflow-hidden">
          <main
            ref={scrollRef}
            onScroll={onScroll}
            className="no-scrollbar relative flex-1 overflow-y-auto overscroll-y-contain px-12 pt-24 pb-24"
            style={{ zoom: "var(--content-zoom, 1)" }}
          >
            <PullToRefresh scrollRef={scrollRef} onRefresh={handleRefresh}>
              {children}
            </PullToRefresh>
          </main>
          <ActionPanel />
        </ResizablePanel>

        {/* AI Chat — collapsible right side panel */}
        <ResizableHandle
          withHandle={aiChatOpen}
          handleSide="left"
          className="group z-20"
          onPillClick={toggleAiChat}
        />
        <ResizablePanel
          panelRef={chatPanelRef}
          elementRef={chatElementRef}
          collapsible
          collapsedSize="0px"
          defaultSize={aiChatOpen ? `${savedChatWidth}px` : "0px"}
          minSize="280px"
          maxSize="600px"
          onResize={handleChatResize}
          className="h-full overflow-hidden"
        >
          <ChatPanel />
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Edge pill — visible when chat is collapsed */}
      {!aiChatOpen && (
        <button
          type="button"
          data-ai-chat
          onClick={toggleAiChat}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center h-12 w-3 cursor-pointer group"
          aria-label="Show AI chat"
        >
          <div
            className="h-10 w-1.5 rounded-full transition-all duration-300 bg-foreground/10 group-hover:bg-foreground/25 group-hover:w-2"
          />
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mobile: Drawer + TopNav + content
// ---------------------------------------------------------------------------

function MobileLayout({ children }: { children: ReactNode }) {
  const sidebarOpen = useViewContext((s) => s.sidebarOpen)
  const setSidebarOpen = useViewContext((s) => s.setSidebarOpen)
  const setSearchOpen = useViewContext((s) => s.setSearchOpen)
  const { pathname } = useLocation()
  const { scrollRef } = useMainScroll()
  const { onScroll } = useScrollbarAutoHide({ externalRef: scrollRef })
  useScrollRestore(scrollRef)
  const { value: isColorVariant } = useSettingBoolean("#theme-variant-color")
  const iconMode = isColorVariant ? ("color" as const) : ("monochrome" as const)

  // Scroll-to-top: Capacitor statusTap listener (iOS) + invisible tap zone
  const { scrollToTop } = useScrollToTop()

  const handleRefresh = useCallback(async () => {
    setSearchOpen(true)
  }, [setSearchOpen])

  // Auto-close drawer on any route change (e.g. browser back button)
  // NavLink clicks already call onNavigate={close}, but this catches
  // programmatic navigation and browser history events.
  useEffect(() => {
    if (sidebarOpen) setSidebarOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on path change
  }, [pathname])

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      {/* Immersive focus banner — extends behind status bar */}
      <FocusBanner />

      <div className="focus-banner-dim relative shrink-0">
        <TopNavBar />
        <SyncStatusProgressBar position="inline" />
      </div>

      {/* Invisible tap zone over status bar — tap to scroll to top (iOS & Android) */}
      <StatusBarTapZone onTap={scrollToTop} />

      <main
        ref={scrollRef}
        onScroll={onScroll}
        className="focus-banner-dim no-scrollbar relative flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain pt-8"
        style={{
          paddingBottom: "calc(100px + var(--safe-area-bottom, 0px))",
          zoom: "var(--content-zoom, 1)",
        }}
      >
        <PullToRefresh scrollRef={scrollRef} onRefresh={handleRefresh}>
          {children}
        </PullToRefresh>
      </main>

      {/* Portal target for TipTap mobile editor toolbar.
         EditorShell portals <MobileToolbar> here so it sits
         directly above ActionPanel in the flex column. */}
      <div id="editor-toolbar-slot" className="shrink-0" />

      <MobileTimerSlot />

      <ActionPanel />

      {/* FAB — mobile only, visible when ActionPanel is hidden (no-selection) */}
      <AddFab />

      {/* Drawer renders as a portal-like overlay */}
      <MobileDrawer>
        <Sidebar iconMode={iconMode} onNavigate={() => setSidebarOpen(false)} />
      </MobileDrawer>

      {/* AI Chat — swipeable drawer from right edge */}
      <MobileChatDrawer>
        <ChatPanel />
      </MobileChatDrawer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MobileTimerSlot — shows GlobalTimerBar above ActionPanel on mobile
// ---------------------------------------------------------------------------

function MobileTimerSlot() {
  const timerTasks = useActiveTimers()
  const { visible: fabVisible } = useFabState()
  const keyboardOpen = useKeyboardStore((s) => s.keyboard?.isOpen ?? false)
  const isDragging = useViewContext((s) => s.isDragging)
  const aiChatOpen = useViewContext((s) => s.aiChatOpen)
  const { search } = useLocation()
  const taskOpen = search.includes("tid=")
  const settingsOpen = search.includes("setting=")

  // Hide timer bar when screen is occupied by other UI
  const hidden =
    timerTasks.length === 0 ||
    keyboardOpen ||
    taskOpen ||
    settingsOpen ||
    isDragging ||
    aiChatOpen

  if (hidden) return null
  return (
    <div
      className={fabVisible ? "shrink-0 pl-3 pr-[84px]" : "shrink-0 px-3"}
      style={{ paddingBottom: "calc(4px + var(--safe-area-bottom, 0px))" }}
    >
      <GlobalTimerBar tasks={timerTasks} />
    </div>
  )
}
